import React, { useCallback, useMemo, useState } from "react";

import { Button, Col, Row } from "react-bootstrap";

import { toLower, upperFirst } from "lodash";
import { useForm } from "react-hook-form";

import {
  AccountFragment,
  AccountInput,
  BusinessAndTaxFragment,
  BusinessAndTaxInput,
  ParticipantStatus,
  ParticipantTypes,
  useUpdateAccountAndBusinessDetailsMutation,
} from "@api/graphql";
import { PenWriteEditIcon } from "@assets/icons";
import { IconLink } from "@components/IconLink";
import { ToastMessage } from "@components/ToastMessage";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import { accountDetailsErrorResolver } from "@components/account_profile/account_details/AccountDetailsResolver";
import { useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import { DetailCell } from "@components/resale_submission/DetailCell";
import { useIsInternalUserCreatingAccount } from "@pages/register/hooks/useIsInternalUserCreatingAccount";
import { UserDetailsInput } from "@pages/register/steps/RegisterFormTypes";
import { AccountDetailsForm } from "@pages/register/steps/UserDetails/AccountDetailsForm";
import { BusinessTaxDetailsForm } from "@pages/register/steps/UserDetails/BusinessTaxDetailsForm";
import { Role } from "@roles";
import { formatValueToDisplayFriendly } from "@util/index";

interface AccountDetailsProps {
  account: AccountFragment;
  business_and_tax: BusinessAndTaxFragment;
  participant_is_reporter?: boolean;
  participant_is_recipient?: boolean;
  participant_types?: (ParticipantTypes | undefined)[];
  participant_id: number | undefined;
}

export const AccountDetailsAccordion: React.FC<AccountDetailsProps> = ({
  account,
  business_and_tax,
  participant_is_reporter,
  participant_is_recipient,
  participant_types,
  participant_id,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(account);
  const [toastVariant, setToastVariant] = useState<"info" | "error" | "success">("info");
  const [currentBusinessAndTax, setCurrentBusinessAndTax] = useState(business_and_tax);

  const [updateAccountAndBusinessDetailsMutation] = useUpdateAccountAndBusinessDetailsMutation();

  const isInternalUser = useIsAuthUserAllowed([Role.VisualArtistAdmin, Role.MemberServices]);

  const isRoyaltyRecipientOnly = (participant_is_recipient && !participant_is_reporter) || false;

  const isInternalUserCreatingAccount = useIsInternalUserCreatingAccount();

  const isAccountActive = useMemo(() => {
    return account.status === ParticipantStatus.ACTIVE;
  }, [account.status]);

  const defaultBusinessTaxValues = {
    company_trading_name: business_and_tax.company_trading_name ?? "",
    abn_acn: business_and_tax.abn_acn ?? "",
    legal_entity_name: business_and_tax.legal_entity_name ?? "",
    is_aus_resident: business_and_tax.is_aus_resident ?? undefined,
    residency_citizenship: business_and_tax.residency_citizenship ?? undefined,
  };

  const {
    control,
    formState: { isValid, errors },
    getValues,
  } = useForm<UserDetailsInput>({
    mode: "onChange",
    // The parameters isJointExistingAccount and isRoyaltyRecipientOnly should match the input provided to the BusinessTaxDetailsForm.
    resolver: (data) =>
      accountDetailsErrorResolver(
        data,
        isArtist,
        isBeneficiary,
        false,
        isRoyaltyRecipientOnly,
        isInternalUserCreatingAccount,
        isAMP,
        isCMO,
        isAccountActive,
        account.status,
        participant_types?.filter((type): type is ParticipantTypes => type !== undefined),
      ),
    defaultValues: {
      account_details: {
        ...currentAccount,
        website: currentAccount.website ?? "",
        socials: currentAccount.socials ?? "",
        year_of_birth: currentAccount.year_of_birth ?? undefined,
        year_of_death: currentAccount.year_of_death ?? undefined,
        is_indigenous: currentAccount.is_indigenous ?? undefined,
      },
      tax_details: defaultBusinessTaxValues,
    },
  });

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const submitHandler = useCallback(async () => {
    if (!participant_id) {
      setToastMessage("Participant ID is missing. Cannot update details.");
      setToastVariant("error");
      setShowToast(true);
      return;
    }
    const data = getValues();
    const cleanedAccountInput = cleanAccountInput(data.account_details);
    const cleanedBusinessAndTaxInput = cleanBusinessAndTaxInput(data.tax_details);

    try {
      const accountResponse = await updateAccountAndBusinessDetailsMutation({
        variables: {
          participantId: participant_id,
          accountInput: cleanedAccountInput,
          businessAndTaxInput: cleanedBusinessAndTaxInput,
        },
      });

      if (accountResponse.data?.updateAccountAndBusinessDetails?.success) {
        const { account, business_and_tax, message } = accountResponse.data.updateAccountAndBusinessDetails;
        setCurrentAccount(account);
        setCurrentBusinessAndTax(business_and_tax);
        setIsEditing(false);
        setToastMessage(message || "Account details updated successfully!");
        setToastVariant("success");
      } else {
        setToastMessage(
          accountResponse.data?.updateAccountAndBusinessDetails?.message || "Failed to update account details",
        );
        setToastVariant("error");
      }
    } catch (error) {
      setToastMessage("An error occurred while updating account details:" + error);
      setToastVariant("error");
    } finally {
      setShowToast(true);
    }
  }, [participant_id, getValues, updateAccountAndBusinessDetailsMutation]);
  const shouldShow = !!participant_id || !!participant_is_recipient || !!participant_is_reporter;
  if (!shouldShow) {
    return null;
  }

  const MERGED_FORMATTERS = [
    {
      type: "business",
      title: "Company name",
      format: (business: BusinessAndTaxFragment) => business.company_trading_name || "-",
    },
    {
      type: "account",
      title: "Account status",
      format: (account: AccountFragment) => formatValueToDisplayFriendly(account.status) || "-",
      readOnly: true,
    },
    {
      type: "business",
      title: "ABN",
      format: (business: BusinessAndTaxFragment) => business.abn_acn || "-",
      readOnly: !isInternalUser,
    },
    {
      type: "business",
      title: "Legal entity name",
      format: (business: BusinessAndTaxFragment) => business.legal_entity_name || "-",
      readOnly: !isInternalUser,
    },
    {
      type: "business",
      title: "Australian resident for tax purposes",
      format: (business: BusinessAndTaxFragment) =>
        business.is_aus_resident !== undefined ? (business.is_aus_resident ? "Yes" : "No") : "-",
    },
    {
      type: "business",
      title: "Residency & citizenship",
      format: (business: BusinessAndTaxFragment) => business.residency_citizenship?.join(", ") || "-",
    },
    {
      type: "account",
      title: "AMP type",
      format: (account: AccountFragment) => account.amp_type || "-",
    },
    {
      type: "account",
      title: "Year of birth",
      format: (account: AccountFragment) => account.year_of_birth || "-",
    },
    {
      type: "account",
      title: "Year of death",
      format: (account: AccountFragment) => account.year_of_death || "-",
      editable: isEditing,
    },
    {
      type: "account",
      title: "Website",
      format: (account: AccountFragment) => account.website || "-",
    },
    {
      type: "account",
      title: "Socials",
      format: (account: AccountFragment) => account.socials || "-",
    },
    {
      type: "account",
      title: "Do you identify as Aboriginal or Torres Strait Islander?",
      format: (account: AccountFragment) => upperFirst(toLower(account.is_indigenous).split("_").join(" ")),
    },
    {
      type: "account",
      title: "Address",
      format: (account: AccountFragment) => formatAddress(account),
    },
    {
      type: "account",
      title: "Suburb / City",
      format: (account: AccountFragment) => account.address?.city || "-",
    },
    {
      type: "account",
      title: "State",
      format: (account: AccountFragment) => account.address?.state || "-",
    },
    {
      type: "account",
      title: "Postcode",
      format: (account: AccountFragment) => account.address?.postcode || "-",
    },
    {
      type: "account",
      title: "Country",
      format: (account: AccountFragment) => account.address?.country || "-",
    },
  ];
  if (participant_types === undefined) {
    return <>Missing Participant Types</>;
  }
  const isArtCentre = participant_types.includes(ParticipantTypes.ART_CENTRE);
  const isBeneficiary = participant_types.includes(ParticipantTypes.BENEFICIARY);
  const isArtist = participant_types.includes(ParticipantTypes.ARTIST);
  const isCMO = participant_types.includes(ParticipantTypes.CMO);
  const isAMP = participant_types.includes(ParticipantTypes.AMP);
  const orderedFields = [
    "Company name",
    "Account status",
    "ABN",
    "Legal entity name",
    "Australian resident for tax purposes",
    "Residency & citizenship",
    "AMP type",
    "Year of birth",
    "Year of death",
    "Do you identify as Aboriginal or Torres Strait Islander?",
    "Website",
    "Socials",
    "Address",
    "Suburb / City",
    "State",
    "Postcode",
    "Country",
  ];

  const excludedFieldsMapping = {
    beneficiary: ["Year of birth"],
    artCentre: ["Year of birth", "Year of death"],
    artistOrBeneficiary: ["AMP type"],
    artCentreCmoAmp: ["Year of birth", "Year of death", "Do you identify as Aboriginal or Torres Strait Islander?"],
  };
  const excludedFields = new Set<string>();

  if (isBeneficiary && !isArtist) {
    excludedFieldsMapping.beneficiary.forEach((field) => excludedFields.add(field));
  }
  if (isArtCentre) {
    excludedFieldsMapping.artCentre.forEach((field) => excludedFields.add(field));
  }
  if ((isArtist || isBeneficiary) && !isAMP) {
    excludedFieldsMapping.artistOrBeneficiary.forEach((field) => excludedFields.add(field));
  }
  if (isArtCentre || isCMO || isAMP) {
    excludedFieldsMapping.artCentreCmoAmp.forEach((field) => excludedFields.add(field));
  }
  const allowedFields = orderedFields.filter((field) => !excludedFields.has(field));

  const filteredFormatters = MERGED_FORMATTERS.filter(({ title }) => allowedFields.includes(title));

  const showASTI = filteredFormatters.some(
    (formatter) => formatter.title === "Do you identify as Aboriginal or Torres Strait Islander?",
  );

  return (
    <>
      <ToastMessage message={toastMessage} show={showToast} setShow={setShowToast} type={toastVariant} />
      <CustomAccordion
        accordionKey={"AccountDetails"}
        contrastOpenStyle={false}
        header={<h5 className="mb-0">Account details</h5>}
        defaultActiveKey={"AccountDetails"}
        customHeaderStyle="py-4"
        preventCloseOnContentClick
        showBottomLine
      >
        <Row>
          {!isEditing ? (
            <>
              <Col md={11}>
                <Row className="gap-4">
                  {filteredFormatters.map(({ type, title, format }) => (
                    <Col md={5} className="d-flex flex-column" key={title}>
                      <DetailCell
                        title={title}
                        value={format(type === "account" ? currentAccount : currentBusinessAndTax)}
                      />
                    </Col>
                  ))}
                </Row>
              </Col>

              <Col md={1} className="d-flex justify-content-end">
                <IconLink to="#" src={PenWriteEditIcon} text="Edit" className="me-2" onClick={handleEditToggle} />
              </Col>
            </>
          ) : (
            <>
              <AccountDetailsForm
                control={control}
                errors={errors}
                isArtist={isArtist}
                isBeneficiary={isBeneficiary}
                isAmp={!!account.amp_type}
                showASTI={showASTI}
                isEditing
                isCMO={isCMO}
                isArtCentre={isArtCentre}
                isAccountActive={isAccountActive}
                accountStatus={account.status}
                participantTypes={participant_types?.filter((type): type is ParticipantTypes => type !== undefined)}
              />
              <BusinessTaxDetailsForm
                control={control}
                errors={errors}
                isJoiningExistingAccount={false}
                isRoyaltyRecipientOnly={isRoyaltyRecipientOnly}
                abnMessage="Please provide your ABN or ACN"
                isInternalUser={isInternalUser}
                isEditing
                isArtist={isArtist}
                isBeneficiary={isBeneficiary}
                isAMP={isAMP}
                isCMO={isCMO}
                isArtCentre={isArtCentre}
                accountStatus={account.status}
                participantTypes={participant_types?.filter((type): type is ParticipantTypes => type !== undefined)}
              />
            </>
          )}
        </Row>

        {isEditing && (
          <Row className="mt-3">
            <Col className="d-flex justify-content-end">
              <Button variant="secondary" onClick={handleCancel} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" disabled={!isValid} onClick={submitHandler}>
                Save
              </Button>
            </Col>
          </Row>
        )}
      </CustomAccordion>
    </>
  );
};

const formatAddress = (account: AccountFragment) => {
  const addressParts = [
    account.address?.address_line_1,
    account.address?.address_line_2,
    account.address?.address_line_3,
  ].filter(Boolean);
  return addressParts.length > 0 ? addressParts.join(", ") : "-";
};

const cleanAccountInput = (input: Partial<AccountFragment>): AccountInput => {
  return {
    address: {
      address_line_1: input.address?.address_line_1 || "",
      address_line_2: input.address?.address_line_2 || "",
      address_line_3: input.address?.address_line_3 || "",
      city: input.address?.city || "",
      state: input.address?.state || "",
      postcode: input.address?.postcode || "",
      country: input.address?.country || "",
    },
    website: input.website ?? "",
    socials: input.socials ?? "",
    amp_type: input.amp_type ? input.amp_type.filter(Boolean) : [],
    year_of_birth: input.year_of_birth ?? undefined,
    year_of_death: input.year_of_death ?? undefined,
    is_indigenous: input.is_indigenous ?? undefined,
  };
};

const cleanBusinessAndTaxInput = (input: Partial<BusinessAndTaxInput>): BusinessAndTaxInput => {
  return {
    abn_acn: input.abn_acn ?? "",
    company_trading_name: input.company_trading_name ?? "",
    is_aus_resident: input.is_aus_resident ?? undefined,
    legal_entity_name: input.legal_entity_name ?? "",
    residency_citizenship: input.residency_citizenship?.length ? input.residency_citizenship : undefined,
  };
};
